/**
 * Signal connect fork (plan signal-connector.md §5, decision #1). Two ways in:
 * link your own account by QR, or use Brain's shared number.
 *
 * Importing a backup is NOT one of them: history attaches to a connected
 * account, so the import lives after linking (the wizard's import step, or the
 * account's card on /sources). An import on its own would create collections
 * belonging to no source, with no live stream to keep them current.
 *
 * The shared-number branch is offered only when the server reports a registered
 * number (`GET /signal/config`, driven by `SIGNAL_SHARED_NUMBER`) — the flow is
 * unusable without one, since its first instruction is "add THIS number to your
 * group". It keeps an "Experimental" badge either way: the cold-start decrypt
 * path has still never been observed end-to-end (Track D, R-COLDSTART), so the
 * flow works as specified but has not been proven against a real cold inbound
 * message. Badge, not a block — the caveat is disclosed, not enforced.
 */

import { useQuery } from '@tanstack/react-query';
import { Phone, QrCode } from 'lucide-react';
import { useState } from 'react';
import { signalSharedApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { ConnectOption } from './connect-option';
import { SignalChatPicker } from './signal-chat-picker';
import { SignalQrLink } from './signal-qr-link';
import { StepSignalShared } from './step-signal-shared';

interface Props {
  onNext: () => void;
}

type Mode = 'fork' | 'byo' | 'shared';

export function StepSignalConnect({ onNext }: Props) {
  const [mode, setMode] = useState<Mode>('fork');
  const [accountId, setAccountId] = useState<string | null>(null);

  const { data: config } = useQuery({
    queryKey: qk.signalSharedConfig,
    queryFn: () => signalSharedApi.config(),
  });
  // Optimistic: a shared number is assumed present, since every real install
  // registers one, so the card never flickers greyed while config loads. Only
  // an explicit "no" from the server disables it — which keeps a local or CI
  // install without the env knob honest rather than offering a flow that 503s.
  const sharedAvailable = config?.configured !== false;

  if (mode === 'byo') {
    return accountId ? (
      <SignalChatPicker accountId={accountId} onDone={onNext} />
    ) : (
      <SignalQrLink onLinked={(id) => setAccountId(id)} onBack={() => setMode('fork')} />
    );
  }

  if (mode === 'shared') {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setMode('fork')}
          className="text-[13px] text-faint transition-colors hover:text-foreground"
        >
          ← Choose a different way to connect
        </button>
        <StepSignalShared onNext={onNext} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-[14px] text-muted-foreground">
        Connect your Signal account — you can import older history once it&apos;s linked.
      </p>

      <ConnectOption
        icon={QrCode}
        tone="green"
        title="Link my Signal"
        body="Scan a QR code to add Brain as a linked device, then choose which groups and DMs to index. Messages are indexed from the moment you connect."
        onClick={() => setMode('byo')}
      />

      <ConnectOption
        icon={Phone}
        title="Use Brain's number"
        body={
          sharedAvailable
            ? "Add Brain's Signal number to a group and it indexes that group for your whole workspace."
            : "Add Brain's Signal number to a group and it indexes that group for your whole workspace. No shared number is registered on this Brain yet."
        }
        onClick={sharedAvailable ? () => setMode('shared') : undefined}
        comingSoon={!sharedAvailable}
        badge={sharedAvailable ? 'Experimental' : 'Unavailable'}
      />
    </div>
  );
}
